import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface CartItem {
  id: number;
  product_id: number;
  option_name: string | null;
  quantity: number;
  price: number;
  product_name: string;
  main_image: string;
  selling_price: number;
  stock: number;
  status: string;
}

interface CartContextType {
  items: CartItem[];
  count: number;
  loading: boolean;
  shippingFee: number;
  addToCart: (productId: number, price: number, optionName?: string, quantity?: number) => Promise<{ success: boolean; error?: string }>;
  updateQuantity: (cartId: number, quantity: number) => Promise<void>;
  removeItem: (cartId: number) => Promise<void>;
  refresh: () => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

// 비로그인 시 localStorage 장바구니
const LOCAL_KEY = 'ws_cart';

function getLocalCart(): CartItem[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
  } catch { return []; }
}

function setLocalCart(items: CartItem[]) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(items));
}

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, member } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [shippingFee, setShippingFee] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    if (token && member) {
      setLoading(true);
      try {
        const res = await fetch('/api/cart', { headers: { Authorization: `Bearer ${token}` }, signal });
        if (res.ok) {
          const data = await res.json();
          if (!signal?.aborted) {
            setItems(data.items || []);
            setShippingFee(data.shipping_fee || 0);
          }
        }
      } catch {}
      if (!signal?.aborted) setLoading(false);
    } else {
      setItems(getLocalCart());
      setLoading(false);
    }
  }, [token, member]);

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, [refresh]);

  const addToCart = async (productId: number, price: number, optionName?: string, quantity: number = 1) => {
    if (token && member) {
      try {
        const res = await fetch('/api/cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ product_id: productId, price, option_name: optionName || null, quantity })
        });
        const data = await res.json();
        if (!res.ok) return { success: false, error: data.detail || '추가 실패' };
        await refresh();
        return { success: true };
      } catch {
        return { success: false, error: '서버 연결 실패' };
      }
    } else {
      // 비로그인: localStorage
      const local = getLocalCart();
      const existing = local.find(i => i.product_id === productId && (i.option_name || '') === (optionName || ''));
      if (existing) {
        existing.quantity += quantity;
      } else {
        local.push({
          id: Date.now(),
          product_id: productId,
          option_name: optionName || null,
          quantity,
          price,
          product_name: '',
          main_image: '',
          selling_price: price,
          stock: 999,
          status: '판매중'
        });
      }
      setLocalCart(local);
      setItems([...local]);
      return { success: true };
    }
  };

  const updateQuantity = async (cartId: number, quantity: number) => {
    if (token && member) {
      await fetch(`/api/cart/${cartId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity })
      });
      await refresh();
    } else {
      const local = getLocalCart();
      const item = local.find(i => i.id === cartId);
      if (item) item.quantity = quantity;
      setLocalCart(local);
      setItems([...local]);
    }
  };

  const removeItem = async (cartId: number) => {
    if (token && member) {
      await fetch(`/api/cart/${cartId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await refresh();
    } else {
      const local = getLocalCart().filter(i => i.id !== cartId);
      setLocalCart(local);
      setItems([...local]);
    }
  };

  return (
    <CartContext.Provider value={{ items, count: items.length, loading, shippingFee, addToCart, updateQuantity, removeItem, refresh }}>
      {children}
    </CartContext.Provider>
  );
};
